<template>
  <VueSlider
    v-model="range"
    class="mx-4 my-3"
    :enable-cross="false"
    :contained="true"
    adsorb
    :min="min"
    :max="max"
    tooltip="none"
    role="slider"
    tooltip-placement="bottom"
    @change="$emit('change')"
    @drag-start="$emit('drag-start')"
    @drag-end="$emit('drag-end')"
    @dragging="$emit('dragging')"
    @error="$emit('error')"
  >
    <template #dot="{ index }">
      <button
        class="cursor-pointer rounded-full border bg-black p-3 shadow-bottom focus:ring-2"
        :tabindex="0"
        :aria-label="$t('price_range.range_handler_label')"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="range[index]"
        @keydown.left.prevent="decrement(index)"
        @keydown.right.prevent="increment(index)"
      ></button>
    </template>

    <template #process="{ style }">
      <div class="vue-slider-process bg-black" :style="[style]" />
    </template>
  </VueSlider>
</template>

<script lang="ts">
import type { WritableComputedRef } from 'vue'
import 'vue-slider-component/dist-css/vue-slider-component.css'
// @ts-expect-error Could not find a declaration file for module 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'. '(...)' implicitly has an 'any' type.
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'

export type RangeTuple = [start: number, end: number]

export default defineComponent({
  name: 'RangeSlider',
  components: {
    VueSlider,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [3, 29999],
    },
    min: {
      type: Number as PropType<number>,
      default: 0,
    },
    max: {
      type: Number as PropType<number>,
      default: 100000,
    },
    valueFormatter: {
      type: Function as PropType<(price: number) => string>,
      default: undefined,
    },
  },
  emits: [
    'change',
    'drag-start',
    'drag-end',
    'dragging',
    'update:modelValue',
    'error',
  ],
  setup(props, { emit }) {
    const range: WritableComputedRef<[]> = computed({
      get() {
        return props.modelValue as []
      },
      set(newValue: []): void {
        emit('update:modelValue', newValue)
      },
    })

    const step = 100

    const decrement = (index: number) => {
      const newRange = [...(range.value as number[])]
      const currentValue = newRange[index]
      if (currentValue === undefined) {
        return
      }
      newRange[index] = Math.max(currentValue - step, props.min)
      range.value = newRange as []
    }

    const increment = (index: number) => {
      const newRange = [...(range.value as number[])]
      const currentValue = newRange[index]
      if (currentValue === undefined) {
        return
      }
      newRange[index] = Math.min(currentValue + step, props.max)
      range.value = newRange as []
    }

    return { range, decrement, increment }
  },
})
</script>

<style lang="postcss">
.vue-slider {
  height: 2px !important;
}
.vue-slider-rail {
  @apply bg-gray;
}
.vue-slider-dot {
  width: 35px !important;
  height: 35px !important;
}

/* This is a exception because the slider is taking in the line height of the text */
.vue-slider button {
  min-height: auto !important;
  padding: 0.75rem !important;
  line-height: 1 !important;
}
</style>
